import { getColorFromCSSVariable } from '@/app/components/search/Utilities';
import { canUseDOM } from '@/app/common/utils/canUseDOM';
import type { LayerProps } from 'react-map-gl/mapbox';
import type { Feature, FeatureCollection } from 'geojson';

/** Radius in metres of the translucent area drawn in place of a Top Secret hotel's exact position. */
export const TSH_CIRCLE_RADIUS = 500;

/** Share of a static preview's height that the Top Secret area circle should take up. */
const TSH_PREVIEW_CIRCLE_HEIGHT_RATIO = 0.6;

/**
 * Smallest share of the radius the hotel is pushed off by. Spreading it evenly over the whole disc
 * leaves about 1% of hotels within 50 m of the centre of their own circle — as pinned as they were
 * before the offset existed — so the draw is confined to the outer annulus instead, evenly within
 * it (MPH-1048).
 */
const TSH_MIN_OFFSET_RATIO = 0.5;

const EARTH_RADIUS_METERS = 6378137;
/**
 * Ground resolution at zoom 0 on the equator, in metres per logical pixel. Mapbox tiles a 512 px
 * world, not the 256 px of the older slippy-map convention, so a Mapbox zoom is one level wider
 * than the same number elsewhere and the divisor here has to be 512. Measured on a live map with
 * `map.project()`: 1.4871 m per pixel at zoom 15, latitude 51.4905, which this predicts as 1.4873
 * where a 256 px world predicts 2.9746 (MPH-1048).
 */
const EQUATOR_METERS_PER_PIXEL = (2 * Math.PI * EARTH_RADIUS_METERS) / 512;

/** 2^31 - 1. Prime, and small enough that every product below stays exact in a double. */
const HASH_MODULUS = 2147483647;

// One Lehmer step. Consecutive inputs come out far apart, so two hotels on the same street do not
// end up with near-identical offsets.
const lehmer = (value: number): number => (value * 48271) % HASH_MODULUS;

/**
 * Two values in [0, 1) derived from a hotel's coordinates. Integer arithmetic only, on purpose:
 * Math.sin and friends are not guaranteed to agree to the last bit across JavaScript engines, and
 * the server and the browser have to land on the same offset or the map URL changes on hydration.
 */
const coordinateDraws = ({ latitude, longitude }: LatLng): [number, number] => {
  // Shifted to non-negative hundred-thousandths of a degree (about a metre), then folded into a
  // single number with a multiplier wider than the longitude range, so neighbouring coordinates
  // stay distinct. Far-apart hotels can still collide once this is reduced below, which only means
  // the two of them get the same offset — harmless, since neither reveals anything about the other.
  const gridLatitude = Math.round(latitude * 1e5) + 9_000_000;
  const gridLongitude = Math.round(longitude * 1e5) + 18_000_000;
  // Kept in [1, HASH_MODULUS - 1] so the sequence never reaches the fixed point at zero, which
  // would put the hotel exactly at the centre of its own circle.
  const seed = lehmer(((gridLatitude * 36_000_001 + gridLongitude) % (HASH_MODULUS - 1)) + 1);
  return [seed / HASH_MODULUS, lehmer(seed) / HASH_MODULUS];
};

/**
 * Centre of the "somewhere in this area" circle shown for a Top Secret hotel: the property's own
 * point pushed off by a bearing and distance derived from those same coordinates, so the hotel sits
 * somewhere inside the circle instead of at its centre (MPH-1048).
 *
 * The offset is deterministic rather than random on purpose. Every view of the same hotel — the
 * static preview, the full-screen map, the next page load — draws the same circle, so there is no
 * sequence of views to average out and recover the real point from. The square root on the distance
 * spreads the property evenly over the ring it can land in rather than bunching it near the inner
 * edge.
 */
export const tshAreaCenter = ({ latitude, longitude }: LatLng): LatLng => {
  const [bearingDraw, distanceDraw] = coordinateDraws({ latitude, longitude });
  const bearing = 2 * Math.PI * bearingDraw;
  const distance =
    TSH_CIRCLE_RADIUS * Math.sqrt(TSH_MIN_OFFSET_RATIO ** 2 + (1 - TSH_MIN_OFFSET_RATIO ** 2) * distanceDraw);
  const latitudeRadians = (latitude * Math.PI) / 180;
  const latitudeOffset = (distance * Math.cos(bearing)) / EARTH_RADIUS_METERS;
  const longitudeOffset = (distance * Math.sin(bearing)) / (EARTH_RADIUS_METERS * Math.cos(latitudeRadians));
  return {
    latitude: latitude + (latitudeOffset * 180) / Math.PI,
    longitude: longitude + (longitudeOffset * 180) / Math.PI,
  };
};

/**
 * Zoom for a static map preview of a Top Secret area, plus the on-screen size of the circle drawn
 * over it. The circle always takes the same share of the image height and the zoom absorbs the fact
 * that a metre covers fewer pixels towards the poles — so the area, rather than a legible street
 * layout, is what the preview reads as at every latitude.
 */
export const getTshPreviewFraming = (center: LatLng, height: number): { zoom: number; diameterPx: number } => {
  const metersPerPixelAtZoom0 = EQUATOR_METERS_PER_PIXEL * Math.cos((center.latitude * Math.PI) / 180);
  const targetDiameterPx = TSH_PREVIEW_CIRCLE_HEIGHT_RATIO * height;
  // Rounded so the URL stays a stable cache key; the circle is then sized from the rounded zoom so
  // the two cannot drift apart.
  const zoom = Math.round(Math.log2((targetDiameterPx * metersPerPixelAtZoom0) / (2 * TSH_CIRCLE_RADIUS)) * 100) / 100;
  return { zoom, diameterPx: (2 * TSH_CIRCLE_RADIUS * 2 ** zoom) / metersPerPixelAtZoom0 };
};

export const hotelTopSecretCircleLayerStyle = (center: LatLng): LayerProps => {
  const color = canUseDOM
    ? getColorFromCSSVariable('--lmn-ds-color-background-static-backgroundBrandPrimary')
    : '#FF0000';
  return {
    id: 'circle-area',
    type: 'circle' as const,
    paint: {
      'circle-stroke-color': color,
      'circle-stroke-width': 2,
      'circle-color': color,
      'circle-opacity': 0.1,
      'circle-radius': {
        type: 'exponential' as const,
        base: 2,
        stops: [
          [0, 0],
          [20, getRadiusInPixels(center, TSH_CIRCLE_RADIUS)],
        ] as [number, number][],
      },
    },
  };
};

export const getCoordinatesFromFeatureCollection = (featureCollection: FeatureCollection) => {
  if (!featureCollection.features || featureCollection.features.length === 0) {
    return { minLng: 0, minLat: 0, maxLng: 0, maxLat: 0 };
  }

  const coordinates = featureCollection.features
    .map((feature: Feature) => {
      if (feature.geometry.type === 'Point') {
        return feature.geometry.coordinates;
      }
      return null;
    })
    .filter((coord): coord is [number, number] => coord !== null);

  if (coordinates.length === 0) {
    return { minLng: 0, minLat: 0, maxLng: 0, maxLat: 0 };
  }

  let minLng = coordinates[0][0];
  let minLat = coordinates[0][1];
  let maxLng = coordinates[0][0];
  let maxLat = coordinates[0][1];

  coordinates.forEach(([lng, lat]) => {
    if (lng < minLng) minLng = lng;
    if (lat < minLat) minLat = lat;
    if (lng > maxLng) maxLng = lng;
    if (lat > maxLat) maxLat = lat;
  });

  return { minLng, minLat, maxLng, maxLat };
};

/**
 * Logical pixels that `radius` metres subtends at zoom 20, the far stop of the circle layer's
 * exponential ramp. Spelled out from the same resolution the preview framing uses, in place of the
 * bare 0.075 it stood in for, so the interactive circle and the preview circle cannot drift apart
 * again (MPH-1048).
 */
const getRadiusInPixels = (center: LatLng, radius: number): number =>
  (radius * 2 ** 20) / (EQUATOR_METERS_PER_PIXEL * Math.cos((center.latitude * Math.PI) / 180));
